import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { Table } from 'primeng/table';
import { MessageService } from 'primeng/api';

import { environment } from 'src/environments/environment';

import { AgentAdminApiService } from 'src/app/services/agent-admin-api.service';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { AgentCustomerProfileListRow, AgentCustomerTier } from 'src/app/shared/agent-admin';

/**
 * API-11 admin tab (Part 3): admin-wide list of every customer currently
 * carrying a tier exception (`tier <> 'standard'`) -- add or remove one
 * without navigating to that customer's own `edit-client` screen. Lives on
 * `/agent-setup`, same bucket as Команда агента/Банківські облікові дані --
 * IT/infra config, not an operational queue (see
 * docs/PROMPT_agent-customer-tier-admin-list.md's "Where this lives").
 *
 * **Complementary to, not a replacement for,**
 * `app-edit-agent-customer-tier-tab` (the per-client form embedded in
 * `edit-client`) -- both read/write the exact same `AgentCustomerProfiles`
 * table via the exact same `PUT /api/agent/admin/customer-tier/:id` route;
 * there is no fork in the data model, only a second screen. That per-client
 * tab is untouched by this component.
 *
 * Mirrors `edit-agent-bank-credentials-tab.component.ts`'s shape (PrimeNG
 * inline row-editing table, single-edit-at-a-time via `editMode`,
 * `uid`/`cloneIndex` bookkeeping, a `uid: -2` sentinel for an unsaved new
 * row, a plain `confirm()` before delete -- no `ConfirmationService`/
 * `p-confirmDialog` anywhere in this codebase) rather than re-deriving the
 * pattern.
 *
 * Unlike that sibling, no separate display-name lookup call is needed here:
 * `getCustomerTierList()` already returns `customer_name`/
 * `account_manager_name`/`override_company_name`/`tier_set_by_name`
 * pre-joined server-side (see `AgentCustomerProfileListRow`'s own doc
 * comment) -- the read-only cells below bind those fields directly.
 */
@Component({
  selector: 'app-edit-agent-customer-tier-list-tab',
  templateUrl: './edit-agent-customer-tier-list-tab.component.html',
  styleUrl: './edit-agent-customer-tier-list-tab.component.css',
  encapsulation: ViewEncapsulation.None
})
export class EditAgentCustomerTierListTabComponent implements OnInit, OnDestroy {

  rows: AgentCustomerProfileListRow[] = [];
  rowsClone: AgentCustomerProfileListRow[] = [];
  mainCompanyId: number = null;

  /** Copied verbatim from `edit-agent-customer-tier-tab.component.ts`'s own
   *  `tierOptions`, minus the `standard` entry -- this screen only ever
   *  holds exceptions (rows with `tier <> 'standard'`). Reused both for the
   *  edit-mode dropdown and the Рівень filter-row dropdown, per
   *  docs/PROMPT_agent-customer-tier-admin-list.md's Table/Filter row
   *  subsections. */
  tierOptions: { label: string; value: AgentCustomerTier }[] = [
    { label: 'Ключовий партнер (key_partner) — максимальна знижка', value: 'key_partner' },
    { label: 'Ключовий клієнт (key_account) — знижка за домовленістю', value: 'key_account' },
    { label: 'Новий (new)', value: 'new' },
    { label: 'Крайовий (edge)', value: 'edge' },
  ];

  /** `true` while any row is being edited -- same single-row-edit-at-a-time
   *  convention as the bank-credentials/team-members siblings. Gates every
   *  other row's pencil/trash buttons plus "Оновити"/"Новий запис". */
  editMode: boolean = false;

  private loadQuery: Subscription = null;
  private saveQuery: Subscription = null;
  private deleteQuery: Subscription = null;

  @ViewChild('ACT1') customerTierTable: Table;

  constructor(
    private agentAdminApi: AgentAdminApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private messageService: MessageService,
    private router: Router,
    private location: Location,
  ) {
  }

  ngOnDestroy(): void {
    if (this.loadQuery) { this.loadQuery.unsubscribe(); }
    if (this.saveQuery) { this.saveQuery.unsubscribe(); }
    if (this.deleteQuery) { this.deleteQuery.unsubscribe(); }
  }

  ngOnInit(): void {
    this.mainCompanyId = this.globalsettingsservice.getMainCompanyId();
    this.loadRows();
  }

  /** No client-side filtering beyond the table's own filter row -- the
   *  backend already filters the base list to exceptions (`tier <>
   *  'standard'`), see `AgentAdminApiService.getCustomerTierList()`. */
  loadRows(): void {
    this.loadQuery =
      this.agentAdminApi.getCustomerTierList().subscribe({
        next: rows => {
          this.rows = rows.map(r => ({ ...r, uid: r.customer_id, cloneIndex: 0 }));
          environment.production || console.table(this.rows);
        },
        error: err => {
          console.error('*** EditAgentCustomerTierListTabComponent loadRows: Error: ' + err);
          this.messageService.add(err?.status === 403
            ? { severity: 'error', summary: 'Недостаточно прав', detail: 'Потрібна роль portal-admin для керування рівнями клієнтів' }
            : { severity: 'error', summary: 'Не вдалося завантажити список', detail: err?.message || 'Unknown error' });
        }
      });
  }

  /**
   * `table.reset()` first, THEN `unshift` (not `push`) -- with pagination
   * and the filter row both live on this table, a plain `push()` can land
   * the new row on a page or behind a filter constraint that isn't
   * currently visible, leaving `editMode: true` with no editable row on
   * screen and every other button (pencil/trash/"Оновити"/"Новий запис")
   * disabled with no way out short of reloading. Confirmed via
   * `node_modules/primeng`'s own `Table.reset()`/`clear()` source rather
   * than assumed: it nulls every filter's `.value`, sets `filteredValue =
   * null`, and resets `first = 0` (page 1) -- exactly what's needed to
   * guarantee the front of `rows` (where `unshift` puts the new row) is
   * visible. The filter-row `<input>`/`<p-dropdown>` elements here are
   * plain/uncontrolled (no `[(ngModel)]`), so `reset()` clears the actual
   * filter constraint either way; the text/selection still displayed in
   * those controls afterward is a separate, purely cosmetic residue, not
   * addressed here.
   */
  addRow(): void {
    this.customerTierTable.reset();
    const newRow: AgentCustomerProfileListRow = {
      uid: -2, cloneIndex: 0, customer_id: null, customer_name: '',
      tier: null, account_manager: null, override_company_id: null, notes: '',
    };
    this.rows.unshift(newRow);
    this.onRowEditInit(newRow);
    this.customerTierTable.initRowEdit(newRow);
  }

  onRowEditInit(row: AgentCustomerProfileListRow): void {
    let newCloneIndex = 0;
    this.rowsClone.forEach(r => { newCloneIndex = Math.max(newCloneIndex, r.cloneIndex); });
    row.cloneIndex = ++newCloneIndex;
    this.rowsClone.push({ ...row });
    this.editMode = true;
  }

  onRowEditCancel(row: AgentCustomerProfileListRow, index: number): void {
    if (row.uid === -2) {
      this.rows.splice(index, 1);
    } else {
      this.rows[index] = { ...this.rowsClone.find(r => r.cloneIndex === row.cloneIndex) };
      this.rows[index].cloneIndex = 0;
    }
    this.rowsClone.splice(this.rowsClone.findIndex(r => r.cloneIndex === row.cloneIndex), 1);
    this.editMode = false;
  }

  onRowEditSave(row: AgentCustomerProfileListRow): void {
    // The sibling pSaveEditableRow directive on the same button already exits
    // the table's own row-edit state synchronously on this click, regardless
    // of validation or the save's outcome below -- editMode must follow that,
    // not our async result, or a validation failure or a rejected save would
    // leave every other row's pencil (and "Оновити"/"Новий запис") locked.
    // Same reasoning as edit-agent-bank-credentials-tab.component.ts's own
    // onRowEditSave.
    this.editMode = false;

    if (!row.customer_id) {
      this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Потрібно обрати компанію' });
      return;
    }
    if (!row.tier) {
      this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Рівень (tier) обов’язковий' });
      return;
    }
    // Frontend-only guard, specific to this screen: nothing stops the backend
    // from accepting tier: 'standard' (the per-client tab still allows it,
    // correctly, there) -- but a row saved as 'standard' here would
    // immediately vanish from this filtered list on the next reload with no
    // explanation. See docs/PROMPT_agent-customer-tier-admin-list.md's "Save"
    // subsection.
    if ((row.tier as string) === 'standard') {
      this.messageService.add({
        severity: 'error', summary: 'Помилка',
        detail: 'Оберіть рівень, відмінний від Стандартного — для стандартного рівня запис у цьому списку не потрібен.'
      });
      return;
    }

    const customerId = row.customer_id;
    this.saveQuery =
      this.agentAdminApi.saveCustomerTier(customerId, row).subscribe({
        next: () => {
          // Reload the whole list rather than patch this row in place -- the
          // backend resolves the picked customer to its Main Company before
          // writing, so a full reload is what shows the correct resolved
          // customer_id/customer_name if an admin picked a Legal Entity by
          // mistake, instead of reimplementing the per-client tab's
          // resolveMainCompany()/isLegalEntity guard a second time here.
          this.loadRows();
        },
        error: err => {
          console.error('*** EditAgentCustomerTierListTabComponent onRowEditSave: Error: ' + err);
          this.messageService.add({ severity: 'error', summary: 'Не вдалося зберегти', detail: err?.error?.message || err?.message || 'Unknown error' });
        }
      });
  }

  deleteRow(row: AgentCustomerProfileListRow): void {
    if (!confirm('Видалити рівень для "' + row.customer_name + '"? Компанія повернеться до стандартного рівня.')) { return; }
    this.deleteQuery =
      this.agentAdminApi.deleteCustomerTier(row.customer_id).subscribe({
        next: () => { this.loadRows(); },
        error: err => {
          console.error('*** EditAgentCustomerTierListTabComponent deleteRow: Error: ' + err);
          this.messageService.add({ severity: 'error', summary: 'Не вдалося видалити', detail: err?.error?.message || err?.message || 'Unknown error' });
        }
      });
  }

  /** Read-only-cell label for `row.tier` -- looks up the same `tierOptions`
   *  list used for editing, so there is a single source of truth for the
   *  label text. Falls back to the raw value if it's somehow not one of the
   *  four options (defensive only -- the backend still accepts `standard`
   *  and the other Phase-8-deferred values are not exposed here). */
  tierLabel(tier: AgentCustomerTier): string {
    return this.tierOptions.find(o => o.value === tier)?.label || tier;
  }

  reloadTable(): void {
    this.loadRows();
  }

  /** Opens `row.customer_id`'s own `edit-client` page in a new tab -- same
   *  `Location.prepareExternalUrl()`-aware idiom as
   *  `CompanyselectorComponent.onEditButton()` and
   *  `EditAgentCustomerTierTabComponent.onOpenMainCompany()`: a bare
   *  `Router.serializeUrl()` path 404s under this app's `HashLocationStrategy`
   *  (`useHash: true`) without that step -- see either of those methods' own
   *  comments for the full explanation. Display-mode only, next to the
   *  read-only Компанія cell's `{{row.customer_name}}` text; the edit-mode
   *  `app-companyselector` picker in that same cell keeps its own
   *  `[showEditButton]="false"` untouched. */
  onOpenClient(row: AgentCustomerProfileListRow): void {
    if (!row.customer_id) { return; }
    const path = this.router.serializeUrl(this.router.createUrlTree(['/edit-client', row.customer_id]));
    window.open(this.location.prepareExternalUrl(path), '_blank');
  }
}
